import { PAGE_BG_STYLE } from "@/lib/pageBg";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Image as ImageIcon, DollarSign, Coins, Copy, Check, ImageDown } from "lucide-react";
import { useEmonRate } from "@/contexts/EmonPriceContext";
import { priceToUsd } from "@/lib/priceUtils";
import { toast } from "sonner";

export default function NftPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  

  const nftId = useMemo(() => (id ? decodeURIComponent(id) : ""), [id]);

  const { usdToEmon } = useEmonRate();

  const { data: nft, isLoading } = useQuery({
    queryKey: ["nft", nftId],
    enabled: Boolean(nftId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nfts")
        .select("id, name, description, image_url, owner_wallet, owner_name, creator_wallet, creator_name, current_price, price")
        .eq("id", nftId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  const usdPrice = nft ? priceToUsd(nft.current_price ?? nft.price) : 0;
  const emonPrice = usdToEmon(usdPrice);

  const formatUsd = (v: number) =>
    new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [copyingImage, setCopyingImage] = useState(false);

  // Always use clean production URL for sharing (displayed to user)
  const shareUrl = useMemo(() => {
    if (!nft) return '';
    return `https://flownft.org/share/nft/${nft.id}`;
  }, [nft]);

  // X share text WITHOUT URL (URL goes in separate parameter to avoid duplication)
  const xShareText = nft ? `🎨 ${nft.name}\n💰 $${formatUsd(usdPrice)} (~${emonPrice.toLocaleString()} EMON)\n\n@emontoken1` : '';
  // General share text WITH URL (for copy-paste)
  const shareText = nft ? `🎨 ${nft.name}\n💰 $${formatUsd(usdPrice)} (~${emonPrice.toLocaleString()} EMON)\n🔗 ${shareUrl}` : '';
  
  // X intent: text in 'text' param, URL in 'url' param (prevents duplication)
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(xShareText)}&url=${encodeURIComponent(shareUrl)}`;
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`🎨 ${nft?.name || ''} • $${formatUsd(usdPrice)}`)}`;

  // OG image URL for copying
  const ogImageUrl = nft ? `https://ojgwihrzolijpetrxjir.supabase.co/functions/v1/nft-og-image?nft=${nft.id}` : '';
  // Universal copy function with iOS-specific handling
  const copyToClipboard = async (text: string, type: 'link' | 'text') => {
    try {
      // Check if iOS (Safari or Chrome)
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS) {
        // iOS requires user gesture and specific handling
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.style.fontSize = '16px'; // Prevent zoom on iOS
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, text.length); // iOS specific
        
        let success = false;
        try {
          success = document.execCommand('copy');
        } catch (err) {
          console.error('execCommand failed:', err);
        }
        document.body.removeChild(textArea);
        
        if (!success) {
          // Ultimate fallback: show prompt to manually copy
          window.prompt('Copy this link:', text);
        }
      } else {
        // Modern browsers
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      }
      
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedText(true);
        setTimeout(() => setCopiedText(false), 2000);
      }
      
      toast.success(type === 'link' ? "Link copied!" : "Text copied!");
      return true;
    } catch (error) {
      console.error('Copy failed:', error);
      // Fallback: show prompt
      window.prompt('Copy this link:', text);
      return false;
    }
  };

  const handleCopyLink = () => copyToClipboard(shareUrl, 'link');
  const handleCopyText = () => copyToClipboard(shareText, 'text');

  // Copy OG image to clipboard (for pasting in X)
  const handleCopyImage = async () => {
    if (!ogImageUrl) return;
    
    setCopyingImage(true);
    try {
      const response = await fetch(ogImageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      
      // Try clipboard API first
      if (navigator.clipboard && 'write' in navigator.clipboard) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ [blob.type]: blob })
          ]);
          setCopiedImage(true);
          setTimeout(() => setCopiedImage(false), 2000);
          toast.success("Image copied! Paste it in X (Ctrl+V)");
          return;
        } catch (clipErr) {
          console.log('Clipboard write failed, using download fallback');
        }
      }
      
      // Fallback: download the image
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nft?.name || 'nft'}-share.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Image downloaded! Upload it to your X post");
    } catch (error) {
      console.error('Image copy failed:', error);
      toast.error("Could not copy image. Try right-clicking the NFT image.");
    } finally {
      setCopyingImage(false);
    }
  };

  const handleOpenX = () => {
    window.open(xUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenTelegram = () => {
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (!nftId) {
      navigate("/marketplace", { replace: true });
      return;
    }

    if (nft?.name) {
      document.title = `${nft.name} | FLOW Marketplace`;
    } else {
      document.title = "NFT | FLOW Marketplace";
    }
  }, [nftId, nft?.name, navigate]);

  return (
    <div className="min-h-screen relative flex flex-col relative" style={PAGE_BG_STYLE}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_40%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.1),transparent_40%)] pointer-events-none" />
      <AppHeader currentPage="marketplace" />

      <main className="flex-1 px-4 pt-20 pb-24 relative z-10">
        <section className="max-w-3xl mx-auto">
          <header className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">NFT Details</h1>
              <p className="text-sm text-muted-foreground">NFT details.</p>
            </div>
            <Button variant="outline" onClick={() => navigate("/marketplace")}>Back to Marketplace</Button>
          </header>

          <article>
            <Card className="bg-card/60 border-border/40 backdrop-blur-sm">
              <CardContent className="p-5 md:p-6">
                {isLoading ? (
                  <div className="grid md:grid-cols-[240px,1fr] gap-6">
                    <Skeleton className="aspect-square w-full rounded-lg" />
                    <div className="space-y-3">
                      <Skeleton className="h-7 w-2/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                      <div className="flex gap-2 pt-2">
                        <Skeleton className="h-6 w-24" />
                        <Skeleton className="h-6 w-28" />
                      </div>
                    </div>
                  </div>
                ) : !nft ? (
                  <div className="text-center py-10">
                    <p className="text-muted-foreground">NFT not found.</p>
                    <Button className="mt-4" variant="hero" onClick={() => navigate("/marketplace")}>Go to Marketplace</Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-[240px,1fr] gap-6">
                    <div className="rounded-lg border border-border/50 bg-muted overflow-hidden flex items-center justify-center aspect-square">
                      {nft.image_url ? (
                        <img
                          src={nft.image_url}
                          alt={`NFT ${nft.name}`}
                          className="max-w-full max-h-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>

                    <div className="space-y-3">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground">{nft.name}</h2>
                      {nft.description ? (
                        <p className="text-sm text-muted-foreground leading-relaxed">{nft.description}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">No description.</p>
                      )}

                      {/* Price */}
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Badge className="bg-green-500/90 text-white border-0 font-bold gap-1">
                          <DollarSign className="w-3 h-3" />
                          {formatUsd(usdPrice)}
                        </Badge>
                        <Badge variant="secondary" className="gap-1">
                          <Coins className="w-3 h-3 text-primary" />
                          ≈ {emonPrice.toLocaleString()} EMON
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Badge variant="secondary">
                          Creator: {nft.creator_name || (nft.creator_wallet ? `${nft.creator_wallet.slice(0, 4)}...${nft.creator_wallet.slice(-4)}` : "—")}
                        </Badge>
                        <Badge variant="outline">
                          Owner: {nft.owner_name || `${nft.owner_wallet.slice(0, 4)}...${nft.owner_wallet.slice(-4)}`}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-4">
                        <Button variant="hero" onClick={() => navigate(`/marketplace?search=${encodeURIComponent(nft.name)}`)}>
                          Open in Marketplace
                        </Button>
                        
                        {/* Share section - simplified */}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleCopyImage}
                          disabled={copyingImage}
                          className="gap-2"
                        >
                          {copiedImage ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <ImageDown className="w-4 h-4" />
                          )}
                          {copyingImage ? 'Downloading...' : copiedImage ? 'Downloaded!' : 'Download Image'}
                        </Button>
                        
                        {/* Tap link to copy - VISIBLE FULL LINK */}
                        <div className="w-full mt-3">
                          <p className="text-xs text-muted-foreground mb-1.5">Tap link to copy:</p>
                          <button
                            onClick={handleCopyLink}
                            className="w-full p-2.5 bg-muted/50 rounded-md text-xs text-primary break-all text-left hover:bg-muted transition-colors cursor-pointer border border-border/50"
                          >
                            {copiedLink ? (
                              <span className="flex items-center gap-2 text-green-500">
                                <Check className="w-3.5 h-3.5" /> Copied!
                              </span>
                            ) : (
                              shareUrl
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </article>
        </section>
      </main>

      <Footer />
    </div>
  );
}
